/**
 * Endorsements & Subjectivities checklists (Policy group).
 *
 * Both are wired to the REAL API (jun-16). Each is a flat CRUD list of
 * `{id, title, is_completed}`:
 *   GET    /v2/submissions/{id}/{kind}/        → flat list
 *   POST   /v2/submissions/{id}/{kind}/        {title, is_completed}
 *   PATCH  /v2/submissions/{id}/{kind}/{id}    {title?, is_completed?}
 *   DELETE /v2/submissions/{id}/{kind}/{id}
 * GET maps into the UI's grouped shape; Save diffs the working copy
 * against the last-GET baseline → POST new / PATCH changed / DELETE
 * removed. The server populates defaults on submission create.
 *
 * **Subjectivities** is one group ("Binding Requirement").
 *
 * **Endorsements** has 4 design groups but the API has NO category
 * field, so the FE recovers each server item's group by matching its
 * title against the catalog (`mocks/data/endorsements.json`). Custom
 * rows aren't in the catalog → they land in an "Additional" group on
 * reload (the server stores only title + is_completed). TODO(Henry):
 * add a `category` field to make this exact (see orion-rewiring-questions.md).
 */
import endorsementsCatalog from "../../mocks/data/endorsements.json";
import { getJsonReal, sendJsonReal } from "../client";

export type ChecklistKind = "endorsements" | "subjectivities";

export interface ChecklistItem {
    id: string;
    label: string;
    /** Secondary line under the label (e.g. Gemini Enrollment terms). */
    description?: string;
    checked: boolean;
    /** Custom rows are underwriter-added: born checked, uncheck = delete. */
    custom?: boolean;
    /** EndorsementType category (endorsements only) — the row's group.
     *  Persisted to the backend so it survives reload (OR jun-22). */
    type?: string;
}

export interface ChecklistGroup {
    id: string;
    title: string;
    /** Add-row input placeholder, per the Figma ("Enter Endorsement", …). */
    placeholder?: string;
    /** EndorsementType this group represents (endorsements only). New rows
     *  added under it are stamped with this type. */
    type?: string;
    items: ChecklistItem[];
}

/** Server row — same shape for both endpoints. `type` is the new
 *  EndorsementType column (endorsements; null on subjectivities). */
interface ChecklistItemWire {
    id: string;
    submission_id: string;
    title: string | null;
    is_completed: boolean;
    type?: string | null;
    created_at: string;
    updated_at: string;
}

function checklistUrl(submissionId: string, kind: ChecklistKind): string {
    // Collection path keeps the trailing slash (FastAPI 307 otherwise).
    return `/v2/submissions/${encodeURIComponent(submissionId)}/${kind}/`;
}

/** Last-GET snapshot per `submissionId:kind`, used to diff on Save. */
const baselines = new Map<string, ChecklistItemWire[]>();
const baselineKey = (submissionId: string, kind: ChecklistKind) => `${submissionId}:${kind}`;

/** Shared Save flush: POST new (non-server ids), PATCH changed, DELETE
 *  removed; untouched rows skipped. The group is intentionally NOT sent
 *  (the API has no category column). */
async function flushDiff(url: string, working: ChecklistItem[], baseline: ChecklistItemWire[]): Promise<void> {
    const baselineById = new Map(baseline.map((w) => [w.id, w]));
    const workingIds = new Set(working.map((i) => i.id));
    const ops: Promise<unknown>[] = [];

    // Only send `type` when the item carries one (endorsements); subjectivities
    // leave it out entirely so their payload is unchanged.
    const body = (item: ChecklistItem) =>
        item.type === undefined
            ? { title: item.label, is_completed: item.checked }
            : { title: item.label, is_completed: item.checked, type: item.type };

    for (const item of working) {
        const existing = baselineById.get(item.id);
        if (!existing) {
            ops.push(sendJsonReal(url, "POST", body(item)));
        } else if (
            existing.is_completed !== item.checked ||
            (existing.title ?? "") !== item.label ||
            (existing.type ?? undefined) !== item.type
        ) {
            ops.push(sendJsonReal(`${url}${encodeURIComponent(item.id)}`, "PATCH", body(item)));
        }
    }
    for (const w of baseline) {
        if (!workingIds.has(w.id)) {
            ops.push(sendJsonReal(`${url}${encodeURIComponent(w.id)}`, "DELETE"));
        }
    }
    await Promise.all(ops);
}

// ───────────────────────── Subjectivities ─────────────────────────────────

const SUBJECTIVITIES_GROUP = { id: "binding-requirement", title: "Binding Requirement", placeholder: "Enter Subjectivity" } as const;

/** FE-only secondary descriptions, applied by title match — the API has
 *  no description column. */
const SUBJECTIVITY_DESCRIPTIONS: Record<string, string> = {
    "Gemini Enrollment": "Must complete Gemini enrollment within 60 days or policy will be cancelled",
};

async function listSubjectivities(submissionId: string): Promise<ChecklistGroup[]> {
    const items = await getJsonReal<ChecklistItemWire[]>(checklistUrl(submissionId, "subjectivities"));
    baselines.set(baselineKey(submissionId, "subjectivities"), items);
    return [
        {
            ...SUBJECTIVITIES_GROUP,
            items: items.map((w) => ({
                id: w.id,
                label: w.title ?? "",
                checked: w.is_completed,
                description: SUBJECTIVITY_DESCRIPTIONS[w.title ?? ""],
            })),
        },
    ];
}

// ───────────────────────── Endorsements ───────────────────────────────────

interface CatalogGroup {
    id: string;
    title: string;
    placeholder?: string;
    items: { label: string; description?: string }[];
}
const ENDORSEMENT_CATALOG = (endorsementsCatalog as { groups: CatalogGroup[] }).groups;

/** Catalog group id ↔ backend EndorsementType. The backend now carries the
 *  category on each row (`type`), so we bucket by it exactly instead of
 *  guessing from the title (OR jun-22). */
const GROUP_TO_TYPE: Record<string, string> = {
    endorsements: "Commercial General Liability",
    "cancel-conditions": "Common Policy Conditions",
    exclusions: "Exclusions",
    notices: "Notices",
    additional: "Additional",
};
const TYPE_TO_GROUP: Record<string, string> = Object.fromEntries(Object.entries(GROUP_TO_TYPE).map(([g, t]) => [t, g]));

/** Group metadata in display order (no items). `type` stamps new rows. */
const ENDORSEMENT_GROUPS = ENDORSEMENT_CATALOG.map((g) => ({ id: g.id, title: g.title, placeholder: g.placeholder, type: GROUP_TO_TYPE[g.id] }));

/** Where custom (non-catalog) endorsements land on reload. */
const ADDITIONAL_GROUP = { id: "additional", title: "Additional", placeholder: "Enter Endorsement", type: GROUP_TO_TYPE.additional } as const;

const norm = (t: string) => t.trim().toLowerCase();
const TITLE_TO_GROUP = new Map<string, string>();
const TITLE_TO_DESC = new Map<string, string>();
const TITLE_ORDER = new Map<string, number>();
ENDORSEMENT_CATALOG.forEach((g) => {
    g.items.forEach((it, idx) => {
        TITLE_TO_GROUP.set(norm(it.label), g.id);
        TITLE_ORDER.set(norm(it.label), idx);
        if (it.description) TITLE_TO_DESC.set(norm(it.label), it.description);
    });
});

async function listEndorsements(submissionId: string): Promise<ChecklistGroup[]> {
    const items = await getJsonReal<ChecklistItemWire[]>(checklistUrl(submissionId, "endorsements"));
    baselines.set(baselineKey(submissionId, "endorsements"), items);

    // Bucket server items by their backend `type` (exact). Legacy rows with
    // no type fall back to the title→group catalog match; still-unknown ones
    // go to "Additional".
    const byGroup = new Map<string, ChecklistItem[]>();
    for (const w of items) {
        const title = w.title ?? "";
        const groupId = (w.type ? TYPE_TO_GROUP[w.type] : undefined) ?? TITLE_TO_GROUP.get(norm(title)) ?? ADDITIONAL_GROUP.id;
        const item: ChecklistItem = {
            id: w.id,
            label: title,
            checked: w.is_completed,
            description: TITLE_TO_DESC.get(norm(title)),
            type: w.type ?? GROUP_TO_TYPE[groupId],
        };
        const bucket = byGroup.get(groupId);
        if (bucket) bucket.push(item);
        else byGroup.set(groupId, [item]);
    }

    const byCatalogOrder = (a: ChecklistItem, b: ChecklistItem) =>
        (TITLE_ORDER.get(norm(a.label)) ?? Number.MAX_SAFE_INTEGER) - (TITLE_ORDER.get(norm(b.label)) ?? Number.MAX_SAFE_INTEGER);

    // All 4 design groups always render (even empty, for the +Add Row);
    // "Additional" only when there are unmatched customs.
    const groups: ChecklistGroup[] = ENDORSEMENT_GROUPS.map((g) => ({ ...g, items: (byGroup.get(g.id) ?? []).sort(byCatalogOrder) }));
    const additional = byGroup.get(ADDITIONAL_GROUP.id);
    if (additional?.length) groups.push({ ...ADDITIONAL_GROUP, items: additional });
    return groups;
}

// ───────────────────────────── Dispatch ───────────────────────────────────

export function listChecklist(submissionId: string | undefined, kind: ChecklistKind): Promise<ChecklistGroup[]> {
    if (!submissionId) {
        return Promise.resolve(kind === "subjectivities" ? [{ ...SUBJECTIVITIES_GROUP, items: [] }] : ENDORSEMENT_GROUPS.map((g) => ({ ...g, items: [] })));
    }
    return kind === "subjectivities" ? listSubjectivities(submissionId) : listEndorsements(submissionId);
}

/** Flush the working copy, then re-GET so the diff `baselines` snapshot is
 *  refreshed and new rows pick up their server ids. Returns the fresh grouped
 *  state for the page to adopt. Without the re-GET the baseline stayed frozen at
 *  the first page load, so consecutive Save cycles diffed against stale data —
 *  a checkbox toggled back to its loaded value produced no PATCH and the change
 *  never persisted (OR-1304); new custom rows also got POSTed twice. */
export async function saveChecklist(
    submissionId: string | undefined,
    kind: ChecklistKind,
    groups: ChecklistGroup[],
): Promise<ChecklistGroup[]> {
    if (!submissionId) return groups;
    const baseline = baselines.get(baselineKey(submissionId, kind)) ?? [];
    await flushDiff(checklistUrl(submissionId, kind), groups.flatMap((g) => g.items), baseline);
    return listChecklist(submissionId, kind);
}
